import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import { websocketClient, type WebSocketMessage } from '@/services/websocket'
import type { Execution, PaginatedResponse } from '@/types'

type ExecutionStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled'

class ExecutionStore {
  executions: Execution[] = []
  selectedExecution: Execution | null = null
  isLoading = false
  error: string | null = null

  // Pagination
  currentPage = 1
  pageSize = 20
  totalExecutions = 0

  // Filters
  searchQuery = ''
  statusFilter: ExecutionStatus | 'all' = 'all'
  agentFilter: string | 'all' = 'all'

  // WebSocket
  private wsHandler: ((data: any) => void) | null = null

  constructor() {
    makeAutoObservable(this)
  }

  // Computed values
  get filteredExecutions() {
    const executions = this.executions || []
    return executions.filter((execution) => {
      const matchesSearch =
        !this.searchQuery ||
        execution.id.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        execution.agent_id.toLowerCase().includes(this.searchQuery.toLowerCase())

      const matchesStatus =
        this.statusFilter === 'all' || execution.status === this.statusFilter

      const matchesAgent =
        this.agentFilter === 'all' || execution.agent_id === this.agentFilter

      return matchesSearch && matchesStatus && matchesAgent
    })
  }

  get hasActiveFilters() {
    return (
      this.searchQuery !== '' ||
      this.statusFilter !== 'all' ||
      this.agentFilter !== 'all'
    )
  }

  get runningExecutions() {
    const executions = this.executions || []
    return executions.filter(
      (e) => e.status === 'running' || e.status === 'queued'
    )
  }

  get completedExecutions() {
    const executions = this.executions || []
    return executions.filter((e) => e.status === 'completed')
  }

  get failedExecutions() {
    const executions = this.executions || []
    return executions.filter((e) => e.status === 'failed')
  }

  get successRate() {
    const executions = this.executions || []
    const total = executions.length
    if (total === 0) return 0
    const completed = this.completedExecutions.length
    return Math.round((completed / total) * 100)
  }

  get averageDuration() {
    const completed = this.completedExecutions.filter((e) => e.duration_ms)
    if (completed.length === 0) return 0
    const total = completed.reduce((sum, e) => sum + (e.duration_ms || 0), 0)
    return Math.round(total / completed.length / 1000) // Convert to seconds
  }

  // Actions
  async fetchExecutions() {
    this.isLoading = true
    this.error = null

    try {
      // Primary: fetch from execution_log (permanent history)
      const historyResp = await apiClient.getExecutionHistory({
        skip: (this.currentPage - 1) * this.pageSize,
        limit: this.pageSize,
      })
      const items = (historyResp.data || []).map((row: any) => ({
        id: row.request_id || row.id,
        agent_id: row.agent_name,
        agent_name: row.agent_name,
        status: row.success ? 'completed' : 'failed',
        prompt: row.prompt,
        result: row.output_summary,
        duration_ms: row.duration_ms,
        cost: row.total_cost_usd,
        tokens_used: row.total_tokens,
        model_used: row.model_used,
        created_at: row.created_at,
        success: row.success,
        source: row.source,
      }))

      runInAction(() => {
        this.executions = items
        this.totalExecutions = historyResp.total || items.length
        this.isLoading = false
      })
    } catch (error: any) {
      // Fallback: try old executions table
      try {
        const response: PaginatedResponse<Execution> = await apiClient.getExecutions({
          skip: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        })
        runInAction(() => {
          this.executions = response.items
          this.totalExecutions = response.total
          this.isLoading = false
        })
      } catch {
        runInAction(() => {
          this.executions = []
          this.error = error.response?.data?.detail || 'Failed to fetch executions'
          this.isLoading = false
        })
      }
    }
  }

  async fetchExecutionById(id: string) {
    this.isLoading = true
    this.error = null

    try {
      const execution = await apiClient.getExecution(id)

      runInAction(() => {
        this.selectedExecution = execution
        // Update in list if present
        const index = this.executions.findIndex((e) => e.id === id)
        if (index !== -1) {
          this.executions[index] = execution
        }
        this.isLoading = false
      })

      return execution
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch execution'
        this.isLoading = false
      })
      return null
    }
  }

  async cancelExecution(id: string) {
    this.isLoading = true
    this.error = null

    try {
      const updatedExecution = await apiClient.cancelExecution(id)

      runInAction(() => {
        this.executions = this.executions.map((execution) =>
          execution.id === id ? updatedExecution : execution
        )
        if (this.selectedExecution?.id === id) {
          this.selectedExecution = updatedExecution
        }
        this.isLoading = false
      })

      return updatedExecution
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to cancel execution'
        this.isLoading = false
      })
      throw error
    }
  }

  async deleteExecution(id: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteExecution(id)

      runInAction(() => {
        this.executions = this.executions.filter((execution) => execution.id !== id)
        this.totalExecutions -= 1
        if (this.selectedExecution?.id === id) {
          this.selectedExecution = null
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete execution'
        this.isLoading = false
      })
      throw error
    }
  }

  // WebSocket for real-time execution updates
  subscribeToExecutionUpdates() {
    if (this.wsHandler) return // Already subscribed

    // Create handler for execution updates
    this.wsHandler = (message: WebSocketMessage) => {
      runInAction(() => {
        const executionId = message.execution_id

        // Update selected execution
        if (this.selectedExecution?.id === executionId) {
          this.selectedExecution = {
            ...this.selectedExecution,
            status: message.status,
            ...(message.data || {}),
          }
        }

        // Update in executions list
        const index = this.executions.findIndex((e) => e.id === executionId)
        if (index !== -1) {
          this.executions[index] = {
            ...this.executions[index],
            status: message.status,
            ...(message.data || {}),
          }
        } else {
          // New execution, refetch list
          this.fetchExecutions()
        }
      })
    }

    // Register handler
    websocketClient.on('execution.update', this.wsHandler)
    websocketClient.subscribe('executions')

    console.log('Subscribed to execution updates')
  }

  unsubscribeFromExecutionUpdates() {
    if (this.wsHandler) {
      websocketClient.off('execution.update', this.wsHandler)
      websocketClient.unsubscribe('executions')
      this.wsHandler = null
      console.log('Unsubscribed from execution updates')
    }
  }

  // Filter actions
  setSearchQuery(query: string) {
    this.searchQuery = query
  }

  setStatusFilter(status: ExecutionStatus | 'all') {
    this.statusFilter = status
  }

  setAgentFilter(agentId: string | 'all') {
    this.agentFilter = agentId
  }

  clearFilters() {
    this.searchQuery = ''
    this.statusFilter = 'all'
    this.agentFilter = 'all'
  }

  // Pagination actions
  setPage(page: number) {
    this.currentPage = page
  }

  setPageSize(size: number) {
    this.pageSize = size
    this.currentPage = 1
  }

  // Utility actions
  clearError() {
    this.error = null
  }

  clearSelectedExecution() {
    this.selectedExecution = null
  }

  cleanup() {
    this.unsubscribeFromExecutionUpdates()
  }
}

export const executionStore = new ExecutionStore()
